import React, { useState } from 'react';
import { Table, Button, Input, Select, Tag, Row, Col } from 'antd';
import { SearchOutlined, WalletOutlined, FilterOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import Swal from 'sweetalert2';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import FullResultModal from '../../components/shared/FullResultModal';

const AccountsList = ({ userType = 'PERSONAL' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState('accountNumber');
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters
  const [accountType, setAccountType] = useState(null);
  const [status, setStatus] = useState(null);
  const [tierLevel, setTierLevel] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchAccounts = async (page = 0, size = 10, isSearch = false) => {
    setLoading(true);
    showLoader();
    try {
      let url = `${customBaseUrl.auditUrl}/admin/customers/accounts?pageNo=${page}&pageSize=${size}`;
      
      if (accountType) url += `&accountType=${accountType}`;
      if (status) url += `&status=${status}`;
      if (tierLevel) url += `&tierLevel=${tierLevel}`;

      if (isSearch && searchText) {
        if (searchType === 'userId') {
          url = `${customBaseUrl.auditUrl}/admin/customers/accounts/${searchText}`;
        } else {
          // Based on instructions: search by Account Number uses /enquiry/{accountNumber}
          url = `${customBaseUrl.auditUrl}/admin/customers/enquiry/${searchText}`;
        }
      }

      const response = await httpGet(url);
      
      let dataArr = [];
      let totalCount = 0;

      const rawData = response?.data || response;

      if (isSearch && searchText) {
        if (rawData && !Array.isArray(rawData)) {
          dataArr = [rawData];
          totalCount = 1;
        } else if (Array.isArray(rawData)) {
          dataArr = rawData;
          totalCount = response?.total || dataArr.length;
        }
      } else {
        dataArr = response?.data || [];
        totalCount = response?.total || dataArr.length;
      }

      setData(dataArr);
      setPagination({ 
        ...pagination, 
        current: (response?.pageNo !== undefined ? response.pageNo : page) + 1, 
        pageSize: size, 
        total: totalCount 
      });
      setHasSearched(true);
    } catch (e) {
      console.error('Error fetching accounts:', e);
      Swal.fire('Error', 'Unable to fetch account records.', 'error');
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAccounts(0, pagination.pageSize, !!searchText);
  };

  const columns = [
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name) => <span style={{ fontWeight: 600 }}>{name || '—'}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Total Accounts',
      dataIndex: 'totalAccounts',
      key: 'totalAccounts',
      render: (count) => <Tag color="blue">{count || 0} Accounts</Tag>,
    },
    {
      title: 'Total Balance',
      dataIndex: 'totalBalance',
      key: 'totalBalance',
      render: (balance) => <span style={{ fontWeight: 700 }}>₦{Number(balance || 0).toLocaleString()}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'customerStatus',
      key: 'customerStatus',
      render: (status) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
          {status || 'INACTIVE'}
        </Tag>
      ),
    },
  ];

  return (
    <div className="accounts-list-wrapper">
      <PageHeadingBanner
        icon={<WalletOutlined />}
        title="Account Portfolio Reports"
        subtitle="Detailed analysis of customer bank accounts and balances"
      />

      <div className="customer-search-bar" style={{ background: '#fff', padding: '20px', borderRadius: '12px', margin: '20px 0', border: '1px solid #f0f0f0' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 8, fontSize: '12px', color: '#8c8c8c' }}>Search Records</div>
            <Input.Group compact>
              <Select 
                defaultValue="accountNumber" 
                style={{ width: '35%', height: '42px' }} 
                onChange={setSearchType}
                value={searchType}
              >
                <Select.Option value="accountNumber">Acc. No</Select.Option>
                <Select.Option value="userId">User ID</Select.Option>
              </Select>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Enter search value..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: '65%', height: '42px' }}
              />
            </Input.Group>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 8, fontSize: '12px', color: '#8c8c8c' }}>Account Type</div>
            <Select
              placeholder="All"
              style={{ width: '100%', height: '42px' }}
              allowClear
              onChange={setAccountType}
              value={accountType}
            >
              <Select.Option value="SAVINGS">Savings</Select.Option>
              <Select.Option value="CURRENT">Current</Select.Option>
              <Select.Option value="LOAN">Loan</Select.Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 8, fontSize: '12px', color: '#8c8c8c' }}>Status</div>
            <Select
              placeholder="All"
              style={{ width: '100%', height: '42px' }}
              allowClear
              onChange={setStatus}
              value={status}
            >
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="INACTIVE">Inactive</Select.Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 8, fontSize: '12px', color: '#8c8c8c' }}>Tier Level</div>
            <Select
              placeholder="All"
              style={{ width: '100%', height: '42px' }}
              allowClear
              onChange={setTierLevel}
              value={tierLevel}
            >
              <Select.Option value="PERSONAL_TIER_1">Tier 1</Select.Option>
              <Select.Option value="PERSONAL_TIER_2">Tier 2</Select.Option>
              <Select.Option value="PERSONAL_TIER_3">Tier 3</Select.Option>
            </Select>
          </Col>
          <Col xs={12} md={4}>
            <div style={{ marginBottom: 8, visibility: 'hidden' }}>Action</div>
            <Button 
              type="primary" 
              onClick={handleSearch}
              icon={<FilterOutlined />}
              style={{ width: '100%', height: '42px', background: '#ff6700', borderColor: '#ff6700', borderRadius: '8px' }}
            >
              Fetch Reports
            </Button>
          </Col>
        </Row>
      </div>

      <div className="customer-table-card">
        {hasSearched ? (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.userId || record.accountNumber}
            loading={loading}
            pagination={pagination}
            onChange={(pag) => fetchAccounts(pag.current - 1, pag.pageSize, !!searchText)}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRecord(record);
                setIsModalOpen(true);
              },
              style: { cursor: 'pointer' }
            })}
            className="customer-table"
          />
        ) : (
          <div style={{ padding: '80px 0', textAlign: 'center', background: '#fff', borderRadius: '12px' }}>
              <WalletOutlined style={{ fontSize: 48, color: '#f0f0f0', marginBottom: 16 }} />
              <h3 style={{ color: '#8c8c8c' }}>Account Data Explorer</h3>
              <p style={{ color: '#bfbfbf' }}>Filter and search to analyze customer account portfolios</p>
          </div>
        )}
      </div>

      <FullResultModal 
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        data={selectedRecord}
        title="Full Account & User Portfolio"
      />
    </div>
  );
};

export default AccountsList;
